
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { supabaseConfig } from '../../firebase.config';
import { ThemeService } from './theme.service';

const SETTINGS_KEY = 'portal_settings';
const CUSTOMER_JWT_KEY = 'customer_jwt';

@Injectable({
  providedIn: 'root',
})
export class CustomerAuthService {
  private router: Router = inject(Router);
  private themeService = inject(ThemeService);
  private supabase: SupabaseClient;

  constructor() {
    if (!supabaseConfig.url || !supabaseConfig.anonKey) {
        throw new Error('Supabase configuration is missing. The customer portal cannot function.');
    }
    
    // DEFINITIVE FIX:
    // For custom JWT authentication, we use a custom fetch wrapper to inject the Authorization header.
    // This bypasses the static type restriction of global.headers and ensures the token is
    // read from localStorage dynamically for every request.
    this.supabase = createClient(supabaseConfig.url, supabaseConfig.anonKey, {
      global: {
        fetch: (url, options) => {
          const token = localStorage.getItem(CUSTOMER_JWT_KEY);
          const headers = new Headers(options?.headers);
          
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          }
          
          return fetch(url, { ...options, headers });
        },
      },
    });
  }
  
  public getSupabaseClient(): SupabaseClient {
    return this.supabase;
  }

  async login(phoneOrCode: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await this.supabase.rpc('customer_login', { 
        p_phone_or_code: phoneOrCode 
      });

      if (error) {
        throw new Error(error.message);
      }
      
      const { token, settings } = data;

      if (!token) {
        throw new Error('Login failed: No token returned from server.');
      }
      
      // Store the custom JWT manually.
      localStorage.setItem(CUSTOMER_JWT_KEY, token);

      if (settings) {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
        this.themeService.applySettings(settings); // Apply theme immediately
      } else {
        localStorage.removeItem(SETTINGS_KEY);
      }
      
      return { success: true };

    } catch (e) {
      const message = e instanceof Error ? e.message : 'An unknown error occurred.';
      // Clean up on failure
      localStorage.removeItem(CUSTOMER_JWT_KEY);
      localStorage.removeItem(SETTINGS_KEY);
      return { success: false, error: message };
    }
  }

  async logout(): Promise<void> {
    localStorage.removeItem(CUSTOMER_JWT_KEY);
    localStorage.removeItem(SETTINGS_KEY);
    this.themeService.clearSettings();
    // No need to call supabase.auth.signOut() as we are not using its session management.
    this.router.navigate(['/portal/login']);
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem(CUSTOMER_JWT_KEY);
    // A simple check is enough. The backend will validate the token on each request.
    return !!token;
  }
}
